import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip } from './ui/kit';

/**
 * Orders flagged paid with no money recorded against them.
 *
 * Each of these customers holds a receipt saying PAID while their statement
 * shows the full amount outstanding. The system cannot say which is right --
 * the money was never recorded anywhere -- so each one is a decision for
 * somebody who can check the bank:
 *
 *   Payment arrived   record it properly; the chasing stops and the sale
 *                     finally reaches the profit and ledger reports
 *   Never arrived     clear the flag; the debt is real and will be chased,
 *                     and somebody should tell the customer their receipt
 *                     was issued in error
 *
 * Highest value first, because the decision worth making today is the one
 * carrying the most money.
 */

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const today = () => new Date().toISOString().slice(0, 10);

export default function PaymentReconciliation({ notify }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);     // order being decided
  const [mode, setMode] = useState(null);     // 'confirm' | 'clear'
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setError(null);
    try { setData(await req('/api/payment-reconciliation')); }
    catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const start = (item, which) => {
    setOpen(item); setMode(which);
    setForm({
      amount: String(item.amount), payment_method: 'bank_transfer',
      paid_on: item.flagged_on ? item.flagged_on.slice(0, 10) : today(),
      reference: '', reason: '',
    });
  };

  const submit = async () => {
    if ((form.reason || '').trim().length < 10) {
      notify?.('Say how you know, in a sentence', 'error'); return;
    }
    setBusy(true);
    try {
      const url = `/api/payment-reconciliation/${open.sales_order_id}/`
        + (mode === 'confirm' ? 'confirm' : 'clear');
      const body = mode === 'confirm'
        ? { amount: Number(form.amount), payment_method: form.payment_method,
            paid_on: form.paid_on, reference: form.reference.trim() || null,
            reason: form.reason.trim() }
        : { reason: form.reason.trim() };
      const r = await req(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      notify?.(r.note || 'Recorded.', 'success');
      setOpen(null); setMode(null);
      await load();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (error) return <Banner tone="danger" title="Could not load">{error}</Banner>;
  if (!data) return null;

  return (
    <div>
      <Card style={{ marginBottom: space(2) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>
              Orders flagged paid with no payment recorded
            </h3>
            <div style={{ fontSize: 12.5, color: color.textSecondary,
                          marginTop: 4 }}>
              {data.note}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 700,
                          color: data.count ? color.danger : color.success }}>
              {data.count}
            </div>
            <div style={{ fontSize: 12.5, color: color.textSecondary }}>
              {naira(data.total_value)}
            </div>
          </div>
        </div>

        {data.count > 0 && (
          <Banner tone="warning" title="Neither figure is wrong">
            The receipt reads a flag somebody set. The statement is computed
            from actual payments and finds none. Only your bank record can say
            which of these customers actually paid.
          </Banner>
        )}
      </Card>

      {data.count === 0 && (
        <Banner tone="success" title="Nothing unexplained">
          Every order flagged as paid has a payment record behind it.
        </Banner>
      )}

      {data.items.map((i) => (
        <Card key={i.sales_order_id} style={{ marginBottom: space(1.5) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 230 }}>
              <div style={{ fontWeight: 700, fontSize: 14.5 }}>
                {i.customer}
              </div>
              <div style={{ fontSize: 12.5, color: color.textSecondary,
                            marginTop: 2 }}>
                {i.order_number} &middot; {i.order_date} &middot; {i.age_days} days old
              </div>
              <div style={{ fontSize: 12, color: color.textMuted, marginTop: 3 }}>
                Flagged <strong>{i.flagged}</strong>
                {i.flagged_on && ` on ${new Date(i.flagged_on).toLocaleString('en-GB')}`}
                {!i.has_invoice && ' · no invoice was ever raised'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>
                {naira(i.amount)}
              </div>
              {i.previously_decided && (
                <Chip tone="neutral">decided before</Chip>
              )}
            </div>
          </div>

          {/* Shown to anyone who can open this screen. Reaching it already
              requires the Payment Reconciliation grant, and so does every
              action on it -- require_module_access on the API checks the
              same thing. Hiding these behind a separate isAdmin() check made
              the screen look broken for a person who had been granted it
              deliberately. The server still decides; a refusal arrives as a
              message you can read, which beats a button that is not there. */}
          <div style={{ display: 'flex', gap: 8, marginTop: space(1.5),
                        flexWrap: 'wrap' }}>
            <Btn size="sm" disabled={busy}
                 onClick={() => start(i, 'confirm')}>
              The money arrived
            </Btn>
            <Btn size="sm" variant="secondary" disabled={busy}
                 onClick={() => start(i, 'clear')}>
              It never arrived
            </Btn>
          </div>
        </Card>
      ))}

      {/* ---- the decision ------------------------------------------------ */}
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(null)}>
          <div className="modal" style={{ maxWidth: 560, width: '94%' }}
               onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>
                {mode === 'confirm' ? 'Record the payment' : 'Clear the flag'}
              </h3>
              <Btn size="sm" variant="secondary" onClick={() => setOpen(null)}>
                Cancel
              </Btn>
            </div>

            <div className="modal-body">
              <div style={{ fontSize: 13, marginBottom: space(1.5) }}>
                <strong>{open.customer}</strong> &middot; {open.order_number}
                {' '}&middot; {naira(open.amount)}
              </div>

              {mode === 'confirm' ? (
                <>
                  <Banner tone="info" title="This writes to the accounts">
                    The payment is recorded against an invoice, so the customer
                    stops being chased and the sale appears in the profit and
                    ledger reports for the first time.
                  </Banner>
                  <div style={{ display: 'grid', gap: 8,
                                margin: `${space(1.5)} 0`,
                                gridTemplateColumns:
                                  'repeat(auto-fit, minmax(150px, 1fr))' }}>
                    <label>
                      <div style={{ fontSize: 11.5, color: color.textSecondary,
                                    marginBottom: 4 }}>
                        Amount received
                      </div>
                      <input style={inputStyle} inputMode="decimal"
                             value={form.amount}
                             onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    </label>
                    <label>
                      <div style={{ fontSize: 11.5, color: color.textSecondary,
                                    marginBottom: 4 }}>
                        Date it arrived
                      </div>
                      <input style={inputStyle} type="date" value={form.paid_on}
                             max={today()}
                             onChange={(e) => setForm({ ...form, paid_on: e.target.value })} />
                    </label>
                    <label>
                      <div style={{ fontSize: 11.5, color: color.textSecondary,
                                    marginBottom: 4 }}>
                        How
                      </div>
                      <select style={inputStyle} value={form.payment_method}
                              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                        <option value="bank_transfer">Bank transfer</option>
                        <option value="cash">Cash</option>
                        <option value="pos">POS</option>
                        <option value="cheque">Cheque</option>
                        <option value="unspecified">Not known</option>
                      </select>
                    </label>
                    <label>
                      <div style={{ fontSize: 11.5, color: color.textSecondary,
                                    marginBottom: 4 }}>
                        Bank reference
                      </div>
                      <input style={inputStyle} value={form.reference}
                             onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                    </label>
                  </div>
                </>
              ) : (
                <Banner tone="danger" title="The customer holds a receipt saying PAID">
                  Clearing this makes the full {naira(open.amount)} owing and
                  it will be chased. Somebody should tell the customer their
                  receipt was issued in error.
                </Banner>
              )}

              <div style={{ marginTop: space(1.5) }}>
                <div style={{ fontSize: 11.5, color: color.textSecondary,
                              marginBottom: 4 }}>
                  {mode === 'confirm'
                    ? 'How do you know it was paid? The bank line, the teller, who confirmed it.'
                    : 'Why was it flagged paid when it was not?'}
                </div>
                <textarea style={{ ...inputStyle, minHeight: 72 }}
                          value={form.reason}
                          onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </div>

              <div style={{ marginTop: space(2) }}>
                <Btn disabled={busy} onClick={submit}>
                  {busy ? 'Recording…'
                        : mode === 'confirm' ? 'Record the payment'
                        : 'Clear the flag'}
                </Btn>
              </div>

              <div style={{ fontSize: 11, color: color.textMuted,
                            marginTop: space(1.5) }}>
                Recorded against your name, permanently. A decision cannot be
                edited later &mdash; a further one is recorded instead.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
